// Domain-Driven Design: Core Domain Models

/**
 * Project is the Aggregate Root
 * All transcriptions, word lists, and beep sounds belong to or are associated with a project
 */
export interface Project {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    // Project configuration
    defaultBeepSoundId?: string; // Global beep sound reference
    defaultWordlistId?: string; // ID of the default wordlist
    // Metadata
    totalTranscriptions: number;
    defaultWordlistName: string;
    lastActivity?: string;
    favorite: boolean; // Whether this project is favorited
    isDefault: boolean; // Whether this is the default project (backend wire field: is_global)
}

/**
 * AudioUpload - a source audio file sitting in R2, belonging to a Project.
 *
 * Uploading is its own user step, and a resumable one: the client cuts the file into fixed-size
 * parts and PUTs each straight to R2 with its own presigned URL (audio never passes through the
 * worker — Cloudflare caps request bodies at 100 MB). R2 remembers which parts landed, so a
 * `pending` row is an upload to carry on rather than a dead one. One upload can be transcribed any
 * number of times, so this is deliberately not 1:1 with Transcription.
 */
export interface AudioUpload {
    id: string;
    projectId: string;
    name: string; // Original filename, e.g. episode-42.mp3
    r2Key: string;
    sizeBytes: number | null; // Recorded by /complete from what R2 actually received
    // What the browser reported when the upload started. `sizeBytes` stays null until R2 confirms
    // what it received, so this is the only size a half-finished upload can show.
    clientSizeBytes: number | null;
    // File.lastModified in epoch ms. Sent back on resume so the server can refuse a different file
    // — R2 would otherwise stitch parts from two recordings into one object and complete it.
    clientLastModified: number | null;
    contentType: string | null;
    duration?: number; // Seconds, probed in the browser before the upload started
    durationTicks?: number; // Premiere ticks (254016000000 per second)
    sampleRate?: number; // Hz, needed by /convertxml
    localAudioPath: string | null; // Path on the user's machine, carried into the conversion step
    status: AudioUploadStatus;
    // The R2 multipart upload behind a `pending` row. Null on rows created before resumable
    // uploads existed — those cannot be carried on, and /parts answers 409 for them.
    r2UploadId: string | null;
    partSize: number | null; // Bytes per part, fixed for the life of this upload
    createdAt: string;
    updatedAt: string;
}

/** `pending` — URL issued, bytes may not have landed. `ready` — confirmed in R2, transcribable. */
export type AudioUploadStatus = 'pending' | 'ready';

/**
 * Transcription - belongs to a Project
 */
export interface Transcription {
    id: string;
    projectId: string;
    name: string;
    audioPath: string; // Name/label of the source media (maps to backend audio_path)
    // The uploaded file's name (backend: audio_filename). Set for every server-side transcription,
    // where audioPath — the path on the user's own machine — is usually null. Either one satisfies
    // the media reference /convertxml requires.
    audioFilename?: string | null;
    csvR2Key: string; // R2 storage key (maps to backend csv_r2_key)
    status: TranscriptionStatus;
    createdAt: string;
    updatedAt: string;
    // Populated when status is 'failed' or 'rejected' — the message AssemblyAI (or the plan-limit
    // check) gave us.
    error?: string | null;
    // The upload this job ran against, so a failed row can be retried without re-uploading.
    // Null on rows created by the older CSV-import flow.
    audioUploadId?: string | null;
    sizeBytes?: number; // CSV size; 0 while processing
    // Metadata from processing.
    // sampleRate and duration describe the SOURCE AUDIO and are required by /convertxml. duration
    // now comes back on the record (the server records it when the upload URL is issued);
    // sampleRate still does not — see docs/BACKEND_REQUESTS.md §1.
    sampleRate?: number;
    duration?: number;
    wordCount?: number;
    censoredWordCount?: number;
}

/**
 * Mirrors the backend's `TranscriptionStatus`. Rows created by the older CSV-import flow are
 * always 'completed'.
 *
 * 'rejected' is terminal exactly like 'failed' — stop polling, there is no CSV — but its seconds
 * are NOT refunded, so it needs its own message rather than being folded into 'failed'.
 */
export type TranscriptionStatus =
    | 'processing'
    | 'completed'
    | 'failed'
    | 'rejected';

/**
 * WordList - can be Global or Project-specific, and Public or Private
 */
export interface WordList {
    id: string;
    name: string;
    words: string[];
    scope: WordListScope;
    projectId?: string; // Only set if scope is 'project'
    createdAt: string;
    updatedAt?: string;
    description?: string;
    isDefault?: boolean; // Only one word list per scope can be default
    isPublic?: boolean; // Whether this wordlist is publicly accessible
    isOfficial?: boolean; // Whether this is an official wordlist (admin only)
    creatorEmail?: string; // Email of the creator (only populated for public wordlists)
    isOwner?: boolean; // Whether the current user owns this wordlist (only populated for user collection)
    isDeleted?: boolean; // Whether this wordlist has been marked as deleted
    wordCount?: number; // For marketplace items, the word count without full words array
    userId?: string; // User ID of the owner
}

export type WordListScope = 'global' | 'project';

/**
 * Conversion - tracks XML conversions with stats
 */
export interface Conversion {
    id: string;
    projectId?: string;
    transcriptionId?: string;
    csvPath: string;
    xmlPath: string; // DEPRECATED: Actually stores R2 key, use xmlR2Key instead
    xmlR2Key: string; // R2 key for the XML file
    xmlSignedUrl?: string; // Signed URL for downloading the XML file (time-limited)
    censoredWords: number;
    totalCuts: number;
    mode: string;
    markers: string;
    beepSoundPath?: string; // Path to the beep sound file used (if mode is 'beep')
    /** @deprecated The FIRST list of the set only (backend: wordlist_id). Use {@link wordListIds}. */
    wordListId?: string;
    /** @deprecated The FIRST list's name only (backend: word_list_name). Use {@link wordListNames}. */
    wordListName?: string;
    /**
     * Every word list this conversion was generated from, in the order they were applied — a
     * conversion combines up to 5 (backend: wordlist_ids). Never null; `[]` means no list was used.
     *
     * Rows written before multi-list conversions shipped were backfilled from `wordlist_id`, so
     * they hold at most one id.
     */
    wordListIds: string[];
    /**
     * The names of {@link wordListIds}, same order and length, **snapshotted at conversion time**
     * (backend: wordlist_names).
     *
     * Show these rather than resolving the ids against the current lists: a list may have been
     * renamed or deleted since, and the snapshot is the only record of what this XML actually
     * censored.
     */
    wordListNames: string[];
    /**
     * The media reference recorded for the XML (backend: audio_path).
     *
     * `null` means the conversion predates the column, not that its XML is broken: we simply never
     * recorded whether the clips it names were named. See `isMediaReferenceUnrecorded`.
     */
    audioPath?: string | null;
    createdAt: string;
}

/**
 * BeepSound - can be Global or Project-specific
 */
export interface BeepSound {
    id: string;
    name: string;
    filePath: string;
    scope: BeepSoundScope;
    projectId?: string; // Only set if scope is 'project'
    duration?: number; // in milliseconds
    createdAt: string;
    updatedAt: string;
    description?: string;
}

export type BeepSoundScope = 'global' | 'project';

/**
 * TranscriptionJob - Queue item for batch transcription processing
 */
export interface TranscriptionJob {
    id: string;
    audioPath: string;
    csvFileName: string;
    projectId: string | null;
    status: 'Pending' | 'Processing' | 'Completed' | 'Failed';
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    errorMessage: string | null;
    elapsedSeconds: number | null;
    output: string | null;
}

/**
 * Project Creation Request
 */
export interface CreateProjectRequest {
    name: string;
    description: string;
}

/**
 * Project Update Request
 */
export interface UpdateProjectRequest {
    id: string;
    name?: string;
    description?: string;
    defaultBeepSoundId?: string;
    wordListIds?: string[];
}

/**
 * Transcription Creation Request
 */
export interface CreateTranscriptionRequest {
    projectId: string;
    name: string;
    audioPath: string;
    csvFileName: string;
}

/**
 * WordList Creation Request
 */
export interface CreateWordListRequest {
    name: string;
    words: string[];
    scope: WordListScope;
    projectId?: string;
    description?: string;
    isPublic?: boolean; // Whether this wordlist should be publicly accessible
}

/**
 * BeepSound Creation Request
 */
export interface CreateBeepSoundRequest {
    name: string;
    filePath: string;
    scope: BeepSoundScope;
    projectId?: string;
    description?: string;
}

/**
 * Billing — Paddle. The worker responds in snake_case; every shape below is the camelCase
 * projection produced at the thunk boundary (see store/slices/subscriptionThunks.ts).
 */
export type BillingCycleName = 'monthly' | 'yearly';

export interface PaddleProduct {
    name: string;
    priceId: string;
    price: number;
    currency: string;
    billingCycle: BillingCycleName;
    features: string[];
}

export interface PaddleProducts {
    monthly: PaddleProduct;
    yearly: PaddleProduct;
}

export type SubscriptionStatus = 'active' | 'trialing' | 'paused' | 'canceled' | 'past_due' | string;

export interface SubscriptionBillingCycle {
    frequency: number;
    interval: string; // 'month' | 'year' | ...
}

export interface SubscriptionManagementUrls {
    updatePaymentMethod?: string;
    cancel?: string;
}

export interface Subscription {
    id?: string;
    status: SubscriptionStatus;
    billingCycle?: SubscriptionBillingCycle;
    nextBilledAt?: string;
    managementUrls: SubscriptionManagementUrls;
}

/**
 * The plan a user is actually on, derived from their subscriptions — never chosen directly.
 */
export type SubscriptionPlan = 'pro_monthly' | 'pro_yearly';

export enum THUNK_STATUS {
    idle = 'idle',
    pending = 'pending',
    fulfilled = 'fulfilled',
    rejected = 'rejected'
}